import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDateTime, calculateDuration } from '@/utils/timeUtils'
import { useAppStore } from '@/store'
import { JobRunRecord } from '@/types/job'
import { JobRunStatusTag } from './JobStatusTag'
import { useTranslation } from '@/i18n'

import { Eye } from 'lucide-react'

type JobRunModalProps = {
  isOpen: boolean
  onClose: () => void
  onRunSelect: (jobRunId: string) => void
}

const JobRunModal = ({ isOpen, onClose, onRunSelect }: JobRunModalProps) => {
  const { jobRuns } = useAppStore()
  const { t } = useTranslation()

  const NoData = (
    <div className="w-full flex justify-center">
      <span className="text-gray-500 dark:text-dark-text-muted">
        {t('jobs:job_run.runs_table.no_data')}
      </span>
    </div>
  )

  const openResults = (run: JobRunRecord) => {
    onRunSelect(run.id)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('jobs:job_run.runs_table.title')}
    >
      <div className="overflow-auto max-h-[500px]">
        <Table>
          <TableHeader className="bg-gray-100 dark:bg-dark-accent">
            <TableRow>
              <TableHead>{t('jobs:job_run.runs_table.started_at')}</TableHead>
              <TableHead>{t('jobs:job_run.runs_table.ended_at')}</TableHead>
              <TableHead>{t('jobs:job_run.runs_table.duration')}</TableHead>
              <TableHead>{t('jobs:job_run.runs_table.status')}</TableHead>
              <TableHead>{t('jobs:job_run.runs_table.results')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobRuns?.length > 0 ? (
              jobRuns.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>{formatDateTime(run.started_at)}</TableCell>
                  <TableCell>
                    {run.ended_at ? formatDateTime(run.ended_at) : NoData}
                  </TableCell>
                  <TableCell>
                    {run.ended_at
                      ? calculateDuration(run.started_at, run.ended_at)
                      : NoData}
                  </TableCell>
                  <TableCell>
                    <JobRunStatusTag status={run.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 items-center">
                      {run.result ? (
                        <Button size="sm" onClick={() => openResults(run)}>
                          <Eye className="w-4 h-4 mr-1" />
                          {t('jobs:job_run.runs_table.show')}
                        </Button>
                      ) : (
                        NoData
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-gray-500 dark:text-dark-text-muted">
                  {t('jobs:job_run.runs_table.no_runs_found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>
          {t('common:buttons.close')}
        </Button>
      </div>
    </Modal>
  )
}

export default JobRunModal
